<template>
  <label class="block my-3">
    {{ t(configs.label) }}
  </label>
  <div v-if="configs.options.displayVariables">
    <VariablesButton
      :variables-list="configs.options.variablesList"
      @on-choose-variable="insertContent"
    />
  </div>
  <div class="p-fluid">
    <div class="codemirror-wrapper">
      <div class="codemirror-header">
        <i
          class="fas fa-expand-alt expand-code-btn"
          :title="t('templateBuilder.codeEditor.expandToModal')"
          @click="openCodeModal"
        />
      </div>
      <VCodeMirror
        ref="codeMirrorEditorRef"
        v-model:value="inputValue"
        :mode="{ name: 'text/html' }"
        @blur="onBlur"
      />
    </div>
  </div>

  <!-- Modal CodeMirror -->
  <CodeMirrorModal
    v-model:visible="showCodeModal"
    v-model:code="inputValue"
    :language="'html'"
    :title="t('templateBuilder.fields.code.modalTitle')"
    :show-variables="configs.options.displayVariables"
    :variables-list="configs.options.variablesList || []"
    @apply="handleModalApply"
  />
</template>

<script lang="ts">
import {
  defineComponent, onMounted, PropType, Ref, ref, watch,
} from 'vue';
import { VCodeMirror } from 'vue3-code-mirror';
import { useI18n } from 'vue-i18n';
import { FieldConfig, ParserFieldObject, Property } from '@/types';

import 'codemirror/mode/htmlmixed/htmlmixed';
import beautifier from 'js-beautify';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import CodeMirrorModal from '@/components/common/CodeMirrorModal.vue';

export default defineComponent({
  name: 'Code',
  components: {
    VariablesButton,
    VCodeMirror,
    CodeMirrorModal,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties ?? []);
    const inputValue: Ref<string> = ref(properties.value[0].value);
    const cursorPosition = ref({ line: 0, ch: 0 });
    const codeMirrorEditorRef = ref();
    const showCodeModal = ref(false);
    let lastScrollPosition = { top: 0, left: 0 };

    const restoreScroll = () => {
      if (codeMirrorEditorRef.value && codeMirrorEditorRef.value?.editor) {
        setTimeout(() => {
          codeMirrorEditorRef.value.editor.scrollTo(lastScrollPosition.left, lastScrollPosition.top);
        }, 200);
      }
    };

    const handleValueChange = () => {
      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = inputValue.value;
        return updatedProperty;
      });

      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    const insertContent = (value: string|number|undefined) => {
      if (codeMirrorEditorRef.value) {
        codeMirrorEditorRef.value.editor.getDoc().replaceRange(value, cursorPosition.value);
        handleValueChange();
      }
    };

    const onBlur = (editor: Record<string, Function>) => {
      lastScrollPosition = { top: editor.getScrollInfo().top, left: editor.getScrollInfo().left };
      cursorPosition.value = {
        line: editor.getCursor().line,
        ch: editor.getCursor().ch,
      };

      const currentValue = inputValue.value;
      let newValue;
      properties.value.map((prop: Property) => {
        newValue = prop.value;
        return prop;
      });

      if (currentValue !== newValue) {
        handleValueChange();
      }
    };

    onMounted(() => {
      if (codeMirrorEditorRef.value) {
        lastScrollPosition = { top: 0, left: 0 };
        codeMirrorEditorRef.value.editor.setOption('theme', 'default');
      }
    });

    watch(() => props.parserValues.properties, (newProperties, oldProperties) => {
      if (newProperties && newProperties[0] && newProperties[0].value !== inputValue.value) {
        inputValue.value = beautifier.html(newProperties[0].value);
        restoreScroll();
      }
    }, { deep: true, immediate: true });

    // Also watch the specific value for additional safety
    watch(() => properties.value[0].value, (values, oldValues) => {
      if (values !== oldValues && values !== inputValue.value) {
        inputValue.value = beautifier.html(values);
        restoreScroll();
      }
    }, { immediate: true });

    const openCodeModal = () => {
      showCodeModal.value = true;
    };

    const handleModalApply = (code: string) => {
      inputValue.value = code;
      handleValueChange();
    };

    return {
      t,
      inputValue,
      codeMirrorEditorRef,
      insertContent,
      onBlur,
      handleValueChange,
      showCodeModal,
      openCodeModal,
      handleModalApply,
    };
  },
});
</script>

<style scoped lang="scss">
:deep() .CodeMirror {
  height: 500px !important;
}

/* Styles pour le wrapper CodeMirror avec bouton d'élargissement */
.codemirror-wrapper {
  position: relative;
  width: 100%;
}

.codemirror-header {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  
  .expand-code-btn {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 6px 8px;
    cursor: pointer;
    color: #607D8B;
    font-size: 12px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f8f9fa;
      color: #495057;
      transform: scale(1.05);
    }
  }
}
</style>
